import { Card } from '../types/memory-game.types';
import { GameDifficulty } from '@/contexts/games/types';
import { GAME_CONFIG, EMOJI_SET } from './game-config';

/**
 * Shuffles an array using Fisher-Yates algorithm
 */
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

/**
 * Generates a shuffled array of cards for the specified difficulty
 */
export function generateCards(difficulty: GameDifficulty): Card[] {
  const config = GAME_CONFIG[difficulty];
  const pairsNeeded = config.pairs;
  
  // Select random emojis for this game
  const selectedEmojis = shuffleArray(EMOJI_SET).slice(0, pairsNeeded);
  
  // Create pairs of cards
  const cards: Card[] = [];
  selectedEmojis.forEach((emoji, index) => {
    // Create two cards for each emoji (a pair)
    cards.push(
      {
        id: index * 2,
        emoji,
        isFlipped: false,
        isMatched: false,
        pairId: index
      },
      {
        id: index * 2 + 1,
        emoji,
        isFlipped: false,
        isMatched: false,
        pairId: index
      }
    );
  });
  
  // Shuffle the cards
  return shuffleArray(cards);
}

/**
 * Finds all unmatched cards with the same emoji as the given card
 */
function findMatchingCards(cards: Card[], targetCard: Card): Card[] {
  return cards.filter(card => 
    card.emoji === targetCard.emoji && 
    !card.isMatched && 
    card.id !== targetCard.id
  );
}

/**
 * Gets a random pair of unmatched cards for hints
 */
export function getRandomUnmatchedPair(cards: Card[]): Card[] {
  const unmatchedCards = cards.filter(card => !card.isMatched && !card.isFlipped);
  const availablePairs: Card[][] = [];
  
  // Group cards by emoji to find pairs
  const emojiGroups: { [emoji: string]: Card[] } = {};
  unmatchedCards.forEach(card => {
    if (!emojiGroups[card.emoji]) {
      emojiGroups[card.emoji] = [];
    }
    emojiGroups[card.emoji].push(card);
  });
  
  // Find pairs (groups with exactly 2 cards)
  Object.values(emojiGroups).forEach(group => {
    if (group.length === 2) {
      availablePairs.push(group);
    }
  });
  
  if (availablePairs.length === 0) {
    return [];
  }
  
  // Return a random pair
  const randomIndex = Math.floor(Math.random() * availablePairs.length);
  return availablePairs[randomIndex];
}
